'use client';

import type { BoxProps } from '@chakra-ui/react';
import { Flex, Grid } from '@chakra-ui/react';
import { useEffect, useRef } from 'react';

import SliderButton from './slider-button';

interface Props<T> {
  each: T[] | readonly T[] | undefined;
  children: (item: NonNullable<T>, index: number) => React.ReactNode;
  itemWidth: BoxProps['width'];
  align?: BoxProps['scrollSnapAlign'];
  centerOnMiddle?: boolean; // Nouveau prop pour centrer sur l'élément du milieu
}

export default function Slider<T>({
  each,
  children,
  itemWidth = 'full',
  align = 'center',
  centerOnMiddle = false,
}: Props<T>) {
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (centerOnMiddle && each && each.length > 0 && sliderRef.current) {
      const middleIndex = Math.floor(each.length / 2); // Calcul de l'index de l'élément du milieu
      const sliderElement = sliderRef.current;
      const itemWidthPx = sliderElement.firstElementChild?.clientWidth || 0; // Largeur d'un élément
      const scrollPosition = middleIndex * itemWidthPx - sliderElement.clientWidth / 2 + itemWidthPx / 2;

      sliderElement.scrollTo({
        left: scrollPosition,
        behavior: 'smooth', // Défilement fluide
      });
    }
  }, [centerOnMiddle, each]);

  return (
    <Grid position="relative" alignItems="center" className="slider-container">
      <SliderButton type="left" />
      <SliderButton type="right" />

      <Flex ref={sliderRef} w="full" gap="4" overflowX="hidden" scrollSnapType="x proximity" className="slider">
        {each?.map(
          (item, index) =>
            item && (
              <Flex
                alignItems="center"
                // eslint-disable-next-line react/no-array-index-key
                key={`slider-e-${index}`}
                flex="0 0 auto"
                flexDir="column"
                width={itemWidth}
                scrollSnapAlign={align}
              >
                {children(item, index)}
              </Flex>
            ),
        )}
      </Flex>
    </Grid>
  );
}
